import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import CenterSelector from '../navigation/CenterSelector';
import UserProfileDropdown from '../navigation/UserProfileDropdown';
import { useAuth } from '../../contexts/AuthContext';

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, userProfile, memberships, signOut, isSuperAdmin, isAdmin, getRoutePrefix } = useAuth();

  const role = userProfile?.role;
  const globalRole = userProfile?.global_role;
  const superAdmin = isSuperAdmin?.();
  const adminUser = isAdmin?.() || superAdmin;

  // Determine active panel from URL
  const isSAPanel = location?.pathname?.startsWith('/sa');
  const isAdminPanel = location?.pathname?.startsWith('/admin');
  const isAppPanel = location?.pathname?.startsWith('/app');

  // Centers for selector
  const availableCenters = memberships?.map(m => ({
    id: m?.center_id,
    name: m?.centers?.name || m?.center_id,
    location: m?.centers?.city || ''
  })) || [];

  const handleCenterChange = (centerId) => {
    console.log('Center changed to:', centerId);
  };

  // ── Super Admin Navigation ──────────────────────────────────────────────
  const superAdminNav = [
    { label: 'Global Dashboard', path: '/sa/dashboard', icon: 'LayoutDashboard' },
    { label: 'Centers', path: '/sa/centers', icon: 'Building' },
    { label: 'All Users', path: '/sa/users', icon: 'UserCog' },
    { label: 'Inventory', path: '/sa/inventory', icon: 'Building2' },
    { label: 'Leads', path: '/sa/leads', icon: 'Target' },
    { label: 'Agreements', path: '/sa/agreements', icon: 'FileCheck' },
    { label: 'Prediction V1', path: '/sa/prediction', icon: 'TrendingUp' },
    { label: 'Reports', path: '/sa/reports', icon: 'BarChart3' },
    { label: 'Activity Log', path: '/sa/activity-log', icon: 'Activity' },
    { label: 'AI Tuning', path: '/sa/ai-tuning', icon: 'Sliders' },
    { label: 'Settings', path: '/sa/settings', icon: 'Settings' },
  ];

  // ── Admin Navigation ────────────────────────────────────────────────────
  const adminNav = [
    { label: 'Operations', path: '/admin/dashboard', icon: 'BarChart3' },
    { label: 'Inventory', path: '/admin/inventory', icon: 'Building2' },
    { label: 'Leads', path: '/admin/leads', icon: 'Target' },
    { label: 'Tenants', path: '/admin/tenants', icon: 'UserCheck' },
    { label: 'Agreements', path: '/admin/agreements', icon: 'FileCheck' },
    { label: 'Users', path: '/admin/users', icon: 'UserCog' },
    { label: 'Prediction', path: '/admin/prediction', icon: 'TrendingUp' },
    { label: 'Reports', path: '/admin/reports', icon: 'BarChart3' },
    { label: 'Notifications', path: '/admin/notifications', icon: 'Bell' },
    { label: 'AI Assistant', path: '/admin/ai-chatbot', icon: 'Bot' },
    { label: 'Activity Log', path: '/admin/activity-log', icon: 'Activity' },
    { label: 'Settings', path: '/admin/settings', icon: 'Settings' },
  ];

  // ── User Navigation ─────────────────────────────────────────────────────
  const userNav = [
    { label: 'Dashboard', path: '/app/dashboard', icon: 'LayoutDashboard' },
    { label: 'Leads', path: '/app/leads', icon: 'Target' },
    { label: 'Inventory', path: '/app/inventory', icon: 'Building2' },
    { label: 'Tenants', path: '/app/tenants', icon: 'UserCheck' },
    { label: 'Agreements', path: '/app/agreements', icon: 'FileCheck' },
    { label: 'Notifications', path: '/app/notifications', icon: 'Bell' },
    { label: 'Settings', path: '/app/settings', icon: 'Settings' },
  ];

  // ── Legacy / Mixed Navigation (for old routes) ──────────────────────────
  const legacyNav = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Inventory', path: '/inventory-management', icon: 'Building2' },
    { label: 'Leads', path: '/leads-pipeline', icon: 'Target' },
    { label: 'Tenants', path: '/tenant-management', icon: 'UserCheck' },
    { label: 'Agreements', path: '/agreements', icon: 'FileCheck' },
    { label: 'Mandates', path: '/mandates', icon: 'FileText' },
    { label: 'Reports', path: '/reports', icon: 'BarChart3' },
    { label: 'Notifications', path: '/notifications', icon: 'Bell' },
    { label: 'Prediction', path: '/prediction', icon: 'TrendingUp' },
    ...(adminUser ? [
      { label: 'Operations', path: '/operations-dashboard', icon: 'Activity' },
      { label: 'Centers', path: '/centers', icon: 'Building' },
      { label: 'User Mgmt', path: '/user-management', icon: 'UserCog' },
      { label: 'AI Assistant', path: '/ai-chatbot', icon: 'Bot' },
      { label: 'AI Tuning', path: '/ai-tuning', icon: 'Sliders' },
    ] : []),
    { label: 'Settings', path: '/settings', icon: 'Settings' },
  ];

  // Pick nav based on current panel
  let navigationItems = legacyNav;
  let panelLabel = 'Propques OS';
  let panelColor = 'bg-primary';

  if (isSAPanel) {
    navigationItems = superAdminNav;
    panelLabel = 'Super Admin';
    panelColor = 'bg-purple-600';
  } else if (isAdminPanel) {
    navigationItems = adminNav;
    panelLabel = 'Admin Panel';
    panelColor = 'bg-blue-600';
  } else if (isAppPanel) {
    navigationItems = userNav;
    panelLabel = 'My Workspace';
    panelColor = 'bg-green-600';
  }

  const isActive = (path) => location?.pathname === path || (path === '/dashboard' && location?.pathname === '/');

  const handleMobileMenuToggle = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const handleNavClick = () => setIsMobileMenuOpen(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // Panel switcher for super_admin
  const panelSwitcher = superAdmin ? (
    <div className="px-3 py-2 border-b border-border">
      <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wide">Switch Panel</p>
      <div className="flex gap-1">
        <Link to="/sa/dashboard" onClick={handleNavClick}
          className={`flex-1 text-center text-xs py-1 rounded ${
            isSAPanel ? 'bg-purple-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}>SA</Link>
        <Link to="/admin/dashboard" onClick={handleNavClick}
          className={`flex-1 text-center text-xs py-1 rounded ${
            isAdminPanel ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}>Admin</Link>
        <Link to="/app/dashboard" onClick={handleNavClick}
          className={`flex-1 text-center text-xs py-1 rounded ${
            isAppPanel ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}>App</Link>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile Menu Toggle */}
      <button
        onClick={handleMobileMenuToggle}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-card shadow-md hover:shadow-lg transition-shadow"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:fixed top-0 left-0 h-full w-64 bg-card border-r border-border
          shadow-sm z-40 flex flex-col
          transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo/Brand */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-border">
          <div className={`w-8 h-8 rounded-lg ${panelColor} flex items-center justify-center`}>
            <Icon name="Building" size={20} color="#FFFFFF" />
          </div>
          <span className="text-lg font-semibold text-foreground">{panelLabel}</span>
        </div>

        {/* Panel Switcher (super admin only) */}
        {panelSwitcher}

        {/* Center Selector */}
        {(superAdmin || adminUser) && availableCenters?.length > 0 && (
          <div className="px-4 py-3 border-b border-border">
            <CenterSelector
              centers={availableCenters}
              onCenterChange={handleCenterChange}
            />
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              onClick={handleNavClick}
              className={`
                flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg
                transition-colors duration-200
                ${isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }
              `}
            >
              <Icon name={item?.icon} size={20} />
              <span className="text-sm font-medium">{item?.label}</span>
            </Link>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="border-t border-border px-3 py-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors duration-200"
          >
            <Icon name="LogOut" size={20} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
          onClick={handleMobileMenuToggle}
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:ml-64 overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-foreground hidden sm:block">Propques OS</h1>
            {isSAPanel && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Super Admin</span>}
            {isAdminPanel && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Admin</span>}
          </div>
          <div className="flex items-center gap-4">
            <UserProfileDropdown user={userProfile || { name: user?.email, role: 'user', avatar: null }} />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;